/**
 * Collapsible Block - Frontend Animation
 *
 * Key decisions:
 *  - details.open = false is deferred to onfinish (not the start of close).
 *    Setting it early triggers the UA stylesheet display:none which kills WAAPI.
 *  - During close, a data-closing attribute drives the icon CSS transition so
 *    the icon flips immediately on click even though [open] stays set.
 *  - commitStyles() + cancel() is called before clearing inline styles and
 *    before toggling details.open, preventing any snap from CSS re-cascade.
 *  - height is the only animated property, in both directions; content stays
 *    opaque so overflow:hidden provides the visual clipping throughout. Fading
 *    as well would flash whenever a reversal starts from a partial opacity.
 *  - an interrupted animation is taken over from the height the panel is
 *    actually at, and the replacement runs for a proportional slice of the
 *    base duration, so a quick open-then-close reads as one continuous move.
 */

const OPEN_DURATION = 400;
const CLOSE_DURATION = 350;
// Floor for a reversal, so a near-instant re-click still animates rather than cutting.
const MIN_DURATION = 120;
const EASING = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';

document.querySelectorAll('.wp-block-brandy-collapsible').forEach((block) => {
	const details = block.querySelector('details');
	if (!details) return;

	const summary = details.querySelector('summary');
	const content = details.querySelector('.collapsible-content');
	if (!summary || !content) return;

	// The block's bottom padding was moved onto <summary> at save time, so the
	// open content has nothing holding it off the bottom edge. Hand the value
	// back to the stylesheet here rather than in save(), which would change the
	// stored markup and invalidate every collapsible already published.
	if (summary.style.paddingBottom && !block.style.getPropertyValue('--collapsible-padding-bottom')) {
		block.style.setProperty('--collapsible-padding-bottom', summary.style.paddingBottom);
	}

	let currentAnimation = null;
	let isOpen = details.open;

	summary.addEventListener('click', (e) => {
		e.preventDefault();
		if (isOpen) {
			animateClose();
		} else {
			animateOpen();
		}
	});

	/**
	 * Read the height the panel is rendering at right now, then drop any
	 * in-flight animation.
	 *
	 * Order matters: cancel() reverts the element to its inline height, which
	 * during an open is still the 0px the open pinned at its start. Measuring
	 * after the cancel therefore reports 0 no matter where the panel visually
	 * is, and the replacement animation collapses into a no-op — the abrupt
	 * stop this function exists to prevent.
	 */
	function takeOverCurrentHeight() {
		const height = content.getBoundingClientRect().height;

		if (currentAnimation) {
			currentAnimation.cancel();
			currentAnimation = null;
		}

		return height;
	}

	/**
	 * A full-distance move gets the base duration; a reversal that only has to
	 * cover part of the panel gets the same fraction of it. Without this the
	 * last few pixels of an interrupted animation still take the full duration,
	 * which reads as a stall.
	 */
	function scaleDuration(base, distance, fullDistance) {
		if (!fullDistance) return MIN_DURATION;

		const ratio = Math.min(1, Math.abs(distance) / fullDistance);
		return Math.max(MIN_DURATION, Math.round(base * ratio));
	}

	function animateOpen() {
		isOpen = true;

		// Measured before details.open flips: opening the element hands the
		// closed-state height:0 over to the CSS height:auto rule, so a read
		// taken afterwards would report the full height and cancel the animation
		// out of existence.
		const startHeight = takeOverCurrentHeight();

		details.removeAttribute('data-closing');
		details.open = true;

		content.style.height = startHeight + 'px';
		content.style.opacity = '1';
		content.style.visibility = 'visible';

		const targetHeight = content.scrollHeight;

		const anim = content.animate(
			[
				{ height: startHeight + 'px' },
				{ height: targetHeight + 'px' },
			],
			{
				duration: scaleDuration(OPEN_DURATION, targetHeight - startHeight, targetHeight),
				easing: EASING,
				fill: 'forwards',
			}
		);
		currentAnimation = anim;

		anim.onfinish = () => {
			if (currentAnimation !== anim) return;
			anim.commitStyles();
			anim.cancel();
			content.style.height = 'auto';
			content.style.opacity = '';
			content.style.visibility = '';
			currentAnimation = null;
		};
	}

	function animateClose() {
		isOpen = false;

		const startHeight = takeOverCurrentHeight();

		// Mark as closing so CSS can flip icon immediately without toggling [open]
		details.setAttribute('data-closing', '');

		// Keep content fully visible; overflow:hidden clips it as height shrinks.
		// Do NOT set details.open = false here — that fires UA display:none which
		// kills the WAAPI animation before it can run a single frame.
		content.style.height = startHeight + 'px';
		content.style.opacity = '1';
		content.style.visibility = 'visible';

		const anim = content.animate(
			[
				{ height: startHeight + 'px' },
				{ height: '0px' },
			],
			{
				duration: scaleDuration(CLOSE_DURATION, startHeight, content.scrollHeight),
				easing: EASING,
				fill: 'forwards',
			}
		);
		currentAnimation = anim;

		anim.onfinish = () => {
			if (currentAnimation !== anim) return;
			anim.commitStyles(); // burn height:0 to inline
			anim.cancel();
			// Toggle details.open BEFORE clearing inline height so the CSS
			// closed-state (height:0) is active before inline override is removed.
			details.open = false;
			details.removeAttribute('data-closing');
			content.style.height = '';
			content.style.opacity = '';
			content.style.visibility = '';
			currentAnimation = null;
		};
	}
});
